/**
 * Program registry — single source of truth for how the frontend maps
 * between:
 *
 *   • URL slug           `tbtp`, `sports`, ...
 *   • Program codes      `TBTP`, `SD` / `RGSH` / `RGSF` / `RGST` / `RGSG`, ...
 *   • Display label      `TBTP`, `Sports`, ...
 *
 * Program codes are the `PROGRAM.` prefix used by JWT roles
 * (e.g. `TBTP.CHANGE_MAKER`, `SD.COORDINATOR`). The Impact Data /
 * maker-checker feature uses this registry to (a) authorize the route
 * per JWT roles and (b) filter the resources returned by
 * `/console/review/resources` down to the ones that belong to the
 * requested program.
 *
 * Sports is the only entry that groups multiple program codes — it
 * covers the original SD program plus the four RGS sub-brands (Hockey,
 * Football, Tennis, Golf). Any role prefixed with one of those codes
 * grants access.
 */

export type ProgramSlug =
  | 'tbtp'
  | 'waste-management'
  | 'women-equity'
  | 'learn-labs'
  | 'sports'
  | 'regenerative-agriculture'
  | 'yoga';

/** All program codes present in JWT roles (the `PROGRAM.` prefix). */
export type ProgramCode =
  | 'TBTP'
  | 'WM'
  | 'WE'
  | 'LL'
  | 'SD'
  | 'RGSH'
  | 'RGSF'
  | 'RGST'
  | 'RGSG'
  | 'RF'
  | 'YOGA';

export interface ProgramRegistryEntry {
  slug: ProgramSlug;
  label: string;
  /** Every code that opens this program. Sports has more than one. */
  codes: ProgramCode[];
}

export const PROGRAM_REGISTRY: ProgramRegistryEntry[] = [
  { slug: 'tbtp', label: 'TBTP', codes: ['TBTP'] },
  { slug: 'waste-management', label: 'Waste Management', codes: ['WM'] },
  { slug: 'women-equity', label: 'Women Equity', codes: ['WE'] },
  { slug: 'learn-labs', label: 'Learn Labs', codes: ['LL'] },
  { slug: 'sports', label: 'Sports', codes: ['SD', 'RGSH', 'RGSF', 'RGST', 'RGSG'] },
  { slug: 'regenerative-agriculture', label: 'Regenerative Agriculture', codes: ['RF'] },
  { slug: 'yoga', label: 'Yoga', codes: ['YOGA'] },
];

const bySlug = new Map<string, ProgramRegistryEntry>(
  PROGRAM_REGISTRY.map((p) => [p.slug, p]),
);

/**
 * Look up a program by its URL slug. Returns `undefined` for unknown
 * slugs so callers can render a NotFound-style response rather than
 * silently defaulting to some other program.
 */
export function getProgramBySlug(slug: string | undefined): ProgramRegistryEntry | undefined {
  if (!slug) return undefined;
  return bySlug.get(slug);
}

/**
 * All role strings that grant access to this program: `ADMIN` plus any
 * role prefixed with one of the program's codes. Used by
 * `ProtectedRoute` — the exact string set is what the router matches
 * against `user.roles`, so we can't just pass "any role starting with
 * X" — we build the concrete list from the codes.
 *
 * Because the sub-role suffix is unbounded (CHANGE_MAKER, COORDINATOR,
 * LEAD, CHECKER, …), we return the bare codes plus `ADMIN`, and the
 * route's access check does prefix matching itself (see
 * `hasProgramAccess` in `impact-data/utils/programResource.ts`).
 * `ProtectedRoute` requires exact role strings, so we don't use it for
 * program gating on Impact Data — the ImpactDataPage itself checks
 * access.
 */
export function programAccessCodes(entry: ProgramRegistryEntry): string[] {
  return entry.codes;
}
